import React, { createContext, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../hooks/useAuthContext';
import useForumState from '../hooks/useForumState';

const ForumContext = createContext();

export const useForumContext = () => {
  const context = useContext(ForumContext);
  if (!context) {
    throw new Error('useForumContext must be used within a ForumContextProvider');
  }
  return context;
};

export const ForumContextProvider = ({ children }) => {
  const { user } = useAuthContext();
  const location = useLocation();
  const forumState = useForumState();
  
  // Clear cache when user logs out
  useEffect(() => {
    if (!user) {
      forumState.clearCache();
    }
  }, [user, forumState.clearCache]);
  
  // Cleanup Firebase listeners when navigating away from forum pages
  useEffect(() => {
    const isForumPage = location.pathname.startsWith('/forum');
    
    if (!isForumPage) {
      // Clean up Firebase listeners when not on forum pages
      forumState.cleanup();
    }
  }, [location.pathname, forumState.cleanup]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      forumState.cleanup();
    };
  }, [forumState.cleanup]);
  
  const contextValue = {
    ...forumState,
    currentUserId: user?.uid || null
  };
  
  return (
    <ForumContext.Provider value={contextValue}>
      {children}
    </ForumContext.Provider>
  );
};

export default ForumContext;
